import React from "react"
import { Modal, Input, Form, Tabs } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faFileExport,
  faFileCode,
  faRobot,
  faChartSimple,
  faFileCsv,
  faDatabase,
} from "@fortawesome/free-solid-svg-icons"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import ExportMapModal from "./ExportMap/ExportMapModal.component"
import { useExportDataModal } from "features/QueryConsole/api/useExportDataModal"
import JsonExportPanel from "./ExportDataModal/panels/JsonExportPanel.component"
import JsonlExportPanel from "./ExportDataModal/panels/JsonlExportPanel.component"
import SpssExportPanel from "./ExportDataModal/panels/SpssExportPanel.component"
import CsvExportPanel from "./ExportDataModal/panels/CsvExportPanel.component"
import DqdExportPanel from "./ExportDataModal/panels/DqdExportPanel.component"

import { QueryInstance } from "types/engine.types"
import { EXPORT_VARIABLE_TYPES } from "types/engineExport.types"

interface ExportDataModalProps {
  queryInstance: QueryInstance | null
}

const ExportDataModal: React.FC<ExportDataModalProps> = ({ queryInstance }) => {
  const hook = useExportDataModal(queryInstance)

  if (!queryInstance) return null

  const tabItems = [
    {
      key: "json",
      label: (
        <span className="flex items-center gap-2">
          <FontAwesomeIcon icon={faFileCode} />
          JSON
        </span>
      ),
      children: (
        <JsonExportPanel
          inputValues={hook.inputValues}
          onInputChange={hook.handleInputChange}
          onExport={hook.handleJSONExport}
          isExporting={hook.isExportingJSON}
        />
      ),
    },
    {
      key: "jsonl",
      label: (
        <span className="flex items-center gap-2">
          <FontAwesomeIcon icon={faRobot} />
          JSONL
        </span>
      ),
      children: (
        <JsonlExportPanel
          inputValues={hook.inputValues}
          onInputChange={hook.handleInputChange}
          onExport={hook.handleJSONLExport}
          isExporting={hook.isExportingJSONL}
        />
      ),
    },
    {
      key: "spss",
      label: (
        <span className="flex items-center gap-2">
          <FontAwesomeIcon icon={faChartSimple} />
          SPSS
        </span>
      ),
      children: (
        <SpssExportPanel
          inputValues={hook.inputValues}
          onInputChange={hook.handleInputChange}
          onExportSPS={hook.handleSPSSExport}
          onExportSAV={hook.handleSAVExport}
          isExportingSPS={hook.isExportingSPS}
          isExportingSAV={hook.isExportingSAV}
          form={hook.spssForm}
          onOpenExportMap={hook.handleOpenExportMap}
        />
      ),
    },
    {
      key: "csv",
      label: (
        <span className="flex items-center gap-2">
          <FontAwesomeIcon icon={faFileCsv} />
          CSV
        </span>
      ),
      children: (
        <CsvExportPanel
          inputValues={hook.inputValues}
          onInputChange={hook.handleInputChange}
          onExport={hook.handleCSVExport}
          isExporting={hook.isExportingCsvDirect}
          form={hook.csvForm}
          onOpenExportMap={hook.handleOpenExportMap}
        />
      ),
    },
    {
      key: "dqd",
      label: (
        <span className="flex items-center gap-2">
          <FontAwesomeIcon icon={faDatabase} />
          DQD
        </span>
      ),
      children: (
        <DqdExportPanel
          inputValues={hook.inputValues}
          onInputChange={hook.handleInputChange}
          onExport={hook.handleDQDExport}
          isExporting={hook.isExportingDQD}
        />
      ),
    },
  ]

  return (
    <>
      <DataQButton onClick={() => hook.setIsModalVisible(true)} type="primary">
        Export
        <FontAwesomeIcon icon={faFileExport} className="ml-2" />
      </DataQButton>

      <Modal
        centered
        title="Export Data"
        open={hook.isModalVisible}
        closable={true}
        onCancel={() => hook.setIsModalVisible(false)}
        footer={null}
        width={780}
        destroyOnClose
      >
        <Form layout="vertical" form={hook.filenameForm}>
          <Form.Item
            label="Filename"
            name="filename"
            rules={[{ required: true, message: "Please enter a Filename" }]}
          >
            <Input
              value={hook.inputValues.filename}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                hook.handleInputChange("filename", e.target.value)
              }
              placeholder="Enter filename"
            />
          </Form.Item>
        </Form>

        <Tabs
          tabPosition="left"
          items={tabItems}
          defaultActiveKey="json"
          className="export-data-tabs"
        />

        <div className="flex justify-end mt-5">
          <DataQButton
            type="secondary"
            onClick={() => hook.setIsModalVisible(false)}
          >
            Cancel
          </DataQButton>
        </div>
      </Modal>

      <ExportMapModal
        open={hook.isExportMapModalOpen}
        onClose={() => hook.setIsExportMapModalOpen(false)}
        queryInstance={queryInstance}
        onExport={hook.handleExportWithMap}
        varType={
          hook.exportMapFormat === "csv"
            ? (hook.inputValues.csvVariableType === EXPORT_VARIABLE_TYPES.NUMBERING
                ? EXPORT_VARIABLE_TYPES.NUMBERING
                : EXPORT_VARIABLE_TYPES.LABELS)
            : (hook.inputValues.variableType === EXPORT_VARIABLE_TYPES.NUMBERING
                ? EXPORT_VARIABLE_TYPES.NUMBERING
                : EXPORT_VARIABLE_TYPES.LABELS)
        }
      />
    </>
  )
}

export default ExportDataModal
